import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

interface OnboardingState {
  /** Whether the welcome screen has been dismissed this session */
  welcomeDismissed: boolean
  /** Whether the onboarding sidebar is collapsed */
  sidebarCollapsed: boolean
  /** Which nudges have been dismissed (by nudge ID) */
  dismissedNudges: string[]
  /** Whether the user has hidden the onboarding nav item */
  onboardingHidden: boolean

  // Actions
  dismissWelcome: () => void
  toggleSidebar: () => void
  collapseSidebar: () => void
  expandSidebar: () => void
  dismissNudge: (nudgeId: string) => void
  isNudgeDismissed: (nudgeId: string) => boolean
  hideOnboarding: () => void
  showOnboarding: () => void
  reset: () => void
}

export const useOnboardingStore = create<OnboardingState>()(
  devtools(
    persist(
      (set, get) => ({
        welcomeDismissed: false,
        sidebarCollapsed: false,
        dismissedNudges: [],
        onboardingHidden: false,

        dismissWelcome: () => set({ welcomeDismissed: true }, false, 'dismissWelcome'),

        toggleSidebar: () =>
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }), false, 'toggleSidebar'),

        collapseSidebar: () => set({ sidebarCollapsed: true }, false, 'collapseSidebar'),

        expandSidebar: () => set({ sidebarCollapsed: false }, false, 'expandSidebar'),

        dismissNudge: (nudgeId) =>
          set(
            (state) => ({
              dismissedNudges: [...state.dismissedNudges, nudgeId],
            }),
            false,
            'dismissNudge'
          ),

        isNudgeDismissed: (nudgeId) => get().dismissedNudges.includes(nudgeId),

        hideOnboarding: () => set({ onboardingHidden: true }, false, 'hideOnboarding'),

        showOnboarding: () => set({ onboardingHidden: false }, false, 'showOnboarding'),

        reset: () =>
          set(
            {
              welcomeDismissed: false,
              sidebarCollapsed: false,
              dismissedNudges: [],
              onboardingHidden: false,
            },
            false,
            'reset'
          ),
      }),
      {
        name: 'onboarding-storage',
        partialize: (state) => ({
          welcomeDismissed: state.welcomeDismissed,
          sidebarCollapsed: state.sidebarCollapsed,
          dismissedNudges: state.dismissedNudges,
          onboardingHidden: state.onboardingHidden,
        }),
      }
    ),
    { name: 'onboarding-store' }
  )
)

export default useOnboardingStore
